import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PLAN_IDS = ['monthly', 'annual'];
const REMIND_WITHIN_DAYS = 7;
const EXPIRED_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export const RenewalBanner = () => {
  const { profiles } = useAuth();
  if (!profiles || profiles.is_lifetime_member || !profiles.membership_expiry) return null;

  const expiry = new Date(profiles.membership_expiry);
  const diff = expiry.getTime() - Date.now();
  const expired = diff <= 0;
  const daysLeft = Math.max(1, Math.ceil(diff / DAY_MS));
  if (!expired && daysLeft > REMIND_WITHIN_DAYS) return null;
  if (expired && (!profiles.membership_type || -diff > EXPIRED_WINDOW_DAYS * DAY_MS)) return null;

  const plan = PLAN_IDS.includes(profiles.membership_type) ? profiles.membership_type : 'monthly';
  const dateText = expiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div
      className={`mb-6 rounded-xl border p-4 sm:p-5 flex flex-wrap items-center gap-4 ${expired ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-300'}`}
      data-testid="renewal-banner"
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${expired ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
        <CalendarClock className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className={`font-semibold ${expired ? 'text-red-800' : 'text-amber-900'}`} data-testid="renewal-banner-title">
          {expired ? 'Your premium has expired' : `Premium expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
        </p>
        <p className={`text-sm ${expired ? 'text-red-700' : 'text-amber-800'}`} data-testid="renewal-banner-message">
          {expired
            ? `It ended on ${dateText}. Renew now to get your premium benefits back.`
            : `Valid till ${dateText}. Extend now and the new days are added on top of your remaining days.`}
        </p>
      </div>
      <Link
        to={`/subscription?plan=${plan}`}
        className="px-5 py-2.5 rounded-full bg-[#0b1b33] text-white text-sm font-semibold hover:bg-[#13284a] transition-colors"
        data-testid="renewal-banner-extend-button"
      >
        {expired ? 'Renew now' : 'Extend now'}
      </Link>
    </div>
  );
};

export default RenewalBanner;
